import { Helmet } from "react-helmet-async";
import Hero from "@/components/Hero";
import StorySection from "@/components/StorySection";
import CategoriesSection from "@/components/CategoriesSection";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import React from "react";

const CraftOfTheMonth = React.lazy(() => import("@/components/CraftOfTheMonth"));
const FeaturedProducts = React.lazy(() => import("@/components/FeaturedProducts"));
const FeaturedArtisans = React.lazy(() =>
  import("@/components/FeaturedArtisans").then(({ FeaturedArtisans }) => ({ default: FeaturedArtisans }))
);
import Testimonials from "@/components/Testimonials";
const Newsletter = React.lazy(() => import("@/components/Newsletter"));
const GiftingPreview = React.lazy(() => import("@/components/GiftingPreview"));

const Index = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Store",
        "name": "Gaonova",
        "url": "https://gaonova.com",
        "logo": "https://gaonova.com/logo.png",
        "image": "https://gaonova.com/og-image.jpg",
        "description": "Authentic handcrafted products from rural Indian artisans. Support village communities with every purchase.",
        "telephone": "+911234567890",
        "priceRange": "₹₹",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Rural Artisan Hub",
          "addressLocality": "Kolkata",
          "addressRegion": "West Bengal",
          "postalCode": "700001",
          "addressCountry": "IN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "telephone": "+911234567890",
          "availableLanguage": ["English", "Hindi"]
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "09:00",
            "closes": "20:00"
          }
        ]
      },
      {
        "@type": "WebSite",
        "name": "Gaonova",
        "url": "https://gaonova.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://gaonova.com/shop?search={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* PRIMARY META TAGS */}
        <title>Gaonova | Handcrafted Village Crafts from Rural Indian Artisans</title>
        <meta name="description" content="Shop authentic handcrafted terracotta, Madhubani art, wooden crafts & sabai grass products. Direct from 34K+ rural artisans. Free shipping ₹999+." />
        <meta name="keywords" content="handcrafted, rural artisans, terracotta pottery, madhubani painting, wooden crafts, sabai grass, indian handicrafts, sustainable home decor" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://gaonova.com" />

        {/* OPEN GRAPH / SOCIAL SHARING */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gaonova.com" />
        <meta property="og:title" content="Gaonova | Handcrafted Village Crafts from Rural India" />
        <meta property="og:description" content="Authentic handcrafted products from 34K+ rural Indian artisans. Terracotta, Madhubani, wooden crafts & more." />
        <meta property="og:image" content="https://gaonova.com/images/og-homepage.jpg" />
        <meta property="og:site_name" content="Gaonova" />

        {/* TWITTER CARD */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gaonova | Handcrafted Village Crafts" />
        <meta name="twitter:description" content="Shop authentic handcrafted products from rural Indian artisans." />
        <meta name="twitter:image" content="https://gaonova.com/images/twitter-homepage.jpg" />

        {/* STRUCTURED DATA: STORE + ORGANIZATION */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Store",
                "@id": "https://gaonova.com/#store",
                "name": "Gaonova",
                "url": "https://gaonova.com",
                "logo": "https://gaonova.com/logo.png",
                "image": "https://gaonova.com/images/og-homepage.jpg",
                "description": "Authentic handcrafted village products from rural Indian artisans: terracotta pottery, Madhubani art, wooden crafts, sabai grass, and more.",
                "priceRange": "₹₹",
                "currenciesAccepted": "INR",
                "paymentAccepted": "Credit Card, Debit Card, UPI, Net Banking, COD",
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": "IN",
                  "addressRegion": "India"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "addressCountry": "IN"
                },
                "telephone": "+91-XXXXXXXXXX",
                "email": "hello@gaonova.com",
                "sameAs": [
                  "https://instagram.com/gaonova",
                  "https://facebook.com/gaonova",
                  "https://twitter.com/gaonova"
                ],
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "https://gaonova.com/shop?search={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "Organization",
                "@id": "https://gaonova.com/#organization",
                "name": "Gaonova",
                "url": "https://gaonova.com",
                "logo": "https://gaonova.com/logo.png",
                "founder": {
                  "@type": "Person",
                  "name": "Sayak Naskar & Arnab Roy"
                },
                "mission": "Empowering rural Indian artisans by connecting their handcrafted products to conscious consumers worldwide"
              },
              {
                "@type": "WebSite",
                "@id": "https://gaonova.com/#website",
                "url": "https://gaonova.com",
                "name": "Gaonova",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "https://gaonova.com/shop?search={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        <main id="main-content">
          <Hero />
          <StorySection />
          <React.Suspense fallback={<div className="w-full h-64 flex items-center justify-center"><p>Loading content...</p></div>}>
            <CraftOfTheMonth />
            <FeaturedArtisans />
            <GiftingPreview />
            <CategoriesSection />
            <FeaturedProducts />
            <Testimonials />
            <Newsletter />
          </React.Suspense>
        </main>

        <Footer />
        <BackToTop />
        <WhatsAppButton variant="floating" />
      </div>
    </>
  );
};

export default Index;
